export const COMPRAS_E_TABLE_COLUMNS_STORAGE_KEY = 'projects_compras_e_table_columns_v1';

export type ComprasETableColumnId =
  | 'key'
  | 'summary'
  | 'parent_key'
  | 'esteira'
  | 'pack'
  | 'issue_type'
  | 'status'
  | 'project_key'
  | 'hours_in_period'
  | 'own_cost_cents'
  | 'third_party_cost_cents'
  | 'cost_cents'
  | 'worklog_count';

export type ComprasETableColumnGroupId = 'identity' | 'hours_cost';

export type ComprasETableColumnOption = {
  id: ComprasETableColumnId;
  label: string;
  group: ComprasETableColumnGroupId;
};

export const COMPRAS_E_TABLE_COLUMN_GROUP_ORDER: ComprasETableColumnGroupId[] = [
  'identity',
  'hours_cost',
];

export const COMPRAS_E_TABLE_COLUMN_GROUP_META: Record<
  ComprasETableColumnGroupId,
  { label: string; hint: string }
> = {
  identity: {
    label: 'Identificação',
    hint: 'Chave, resumo, pai, esteira e metadados do ticket.',
  },
  hours_cost: {
    label: 'Horas e custos',
    hint: 'HH no período e custos do recorte.',
  },
};

export const COMPRAS_E_TABLE_COLUMN_OPTIONS: ComprasETableColumnOption[] = [
  { id: 'key', label: 'Chave', group: 'identity' },
  { id: 'summary', label: 'Resumo', group: 'identity' },
  { id: 'parent_key', label: 'Pai', group: 'identity' },
  { id: 'esteira', label: 'Esteira', group: 'identity' },
  { id: 'pack', label: 'SAP', group: 'identity' },
  { id: 'issue_type', label: 'Tipo', group: 'identity' },
  { id: 'status', label: 'Status', group: 'identity' },
  { id: 'project_key', label: 'Projeto', group: 'identity' },
  { id: 'hours_in_period', label: 'HH no período', group: 'hours_cost' },
  { id: 'own_cost_cents', label: 'Custo próprio', group: 'hours_cost' },
  { id: 'third_party_cost_cents', label: 'Custo terceiro', group: 'hours_cost' },
  { id: 'cost_cents', label: 'Custo total', group: 'hours_cost' },
  { id: 'worklog_count', label: 'Worklogs', group: 'hours_cost' },
];

export const COMPRAS_E_DEFAULT_TABLE_COLUMNS: ComprasETableColumnId[] = (
  COMPRAS_E_TABLE_COLUMN_OPTIONS.map((item) => item.id)
);

const COMPRAS_E_TABLE_COLUMN_IDS_SET = new Set<string>(COMPRAS_E_DEFAULT_TABLE_COLUMNS);

export type ComprasETableColumnGroupView = {
  id: ComprasETableColumnGroupId;
  label: string;
  hint: string;
  columns: ComprasETableColumnOption[];
};

type StoredTableColumns = {
  selected?: unknown;
  catalogIds?: unknown;
};

const catalogOrder = (ids: Iterable<string>): ComprasETableColumnId[] => {
  const allowed = new Set(ids);
  return COMPRAS_E_DEFAULT_TABLE_COLUMNS.filter((id) => allowed.has(id));
};

export const sanitizeComprasETableColumns = (value: unknown): ComprasETableColumnId[] => {
  const asArray = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  asArray.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const id = entry.trim();
    if (!id || !COMPRAS_E_TABLE_COLUMN_IDS_SET.has(id) || seen.has(id)) return;
    seen.add(id);
  });
  const ordered = catalogOrder(seen);
  return ordered.length ? ordered : [...COMPRAS_E_DEFAULT_TABLE_COLUMNS];
};

/** Coluna nova no catálogo entra visível; coluna que o usuário já tinha visto e ocultou permanece oculta. */
export const appendMissingComprasETableColumns = (
  selected: string[],
  knownCatalogIds: readonly string[] = COMPRAS_E_DEFAULT_TABLE_COLUMNS,
): ComprasETableColumnId[] => {
  const sanitized = sanitizeComprasETableColumns(selected);
  const known = new Set(knownCatalogIds);
  const extra = COMPRAS_E_DEFAULT_TABLE_COLUMNS.filter(
    (id) => !sanitized.includes(id) && !known.has(id),
  );
  if (!extra.length) return sanitized;
  return catalogOrder([...sanitized, ...extra]);
};

export const toggleComprasETableColumn = (
  selected: string[],
  columnId: string,
): ComprasETableColumnId[] => {
  const sanitized = sanitizeComprasETableColumns(selected);
  if (!COMPRAS_E_TABLE_COLUMN_IDS_SET.has(columnId)) return sanitized;
  if (sanitized.includes(columnId as ComprasETableColumnId)) {
    if (sanitized.length <= 1) return sanitized;
    return sanitized.filter((id) => id !== columnId);
  }
  return catalogOrder([...sanitized, columnId]);
};

export const filterComprasETableColumnOptions = (query: string): ComprasETableColumnOption[] => {
  const term = query.trim().toLowerCase();
  if (!term) return COMPRAS_E_TABLE_COLUMN_OPTIONS;
  return COMPRAS_E_TABLE_COLUMN_OPTIONS.filter((column) => (
    column.label.toLowerCase().includes(term) || column.id.toLowerCase().includes(term)
  ));
};

export const groupedComprasETableColumnOptions = (
  options: ComprasETableColumnOption[],
): ComprasETableColumnGroupView[] => (
  COMPRAS_E_TABLE_COLUMN_GROUP_ORDER.flatMap((groupId) => {
    const columns = options.filter((column) => column.group === groupId);
    if (!columns.length) return [];
    const meta = COMPRAS_E_TABLE_COLUMN_GROUP_META[groupId];
    return [{ id: groupId, label: meta.label, hint: meta.hint, columns }];
  })
);

export const visibleComprasETableColumns = <T extends { key: string }>(
  columns: T[],
  selected: string[],
): T[] => {
  const allowed = new Set<string>(sanitizeComprasETableColumns(selected));
  const visible = columns.filter((column) => allowed.has(column.key));
  if (visible.length) return visible;
  const fallbackId = COMPRAS_E_DEFAULT_TABLE_COLUMNS[0];
  const fallback = columns.filter((column) => column.key === fallbackId);
  return fallback.length ? fallback : columns.slice(0, 1);
};

export const resolveComprasETableSortColumn = (
  sortColumn: string,
  selected: string[],
): string => {
  const visible = sanitizeComprasETableColumns(selected);
  if (visible.includes(sortColumn as ComprasETableColumnId)) return sortColumn;
  return visible[0] || COMPRAS_E_DEFAULT_TABLE_COLUMNS[0];
};

const parseStoredTableColumns = (raw: string | null): ComprasETableColumnId[] | null => {
  if (raw == null) return null;
  const parsed = JSON.parse(raw) as unknown;
  if (Array.isArray(parsed)) {
    return appendMissingComprasETableColumns(
      sanitizeComprasETableColumns(parsed),
      COMPRAS_E_DEFAULT_TABLE_COLUMNS,
    );
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const stored = parsed as StoredTableColumns;
  const known = Array.isArray(stored.catalogIds)
    ? stored.catalogIds.filter((id): id is string => typeof id === 'string')
    : COMPRAS_E_DEFAULT_TABLE_COLUMNS;
  return appendMissingComprasETableColumns(sanitizeComprasETableColumns(stored.selected), known);
};

export const loadComprasETableColumns = (): ComprasETableColumnId[] => {
  if (typeof window === 'undefined') {
    return [...COMPRAS_E_DEFAULT_TABLE_COLUMNS];
  }
  try {
    const loaded = parseStoredTableColumns(
      window.localStorage.getItem(COMPRAS_E_TABLE_COLUMNS_STORAGE_KEY),
    );
    return loaded || [...COMPRAS_E_DEFAULT_TABLE_COLUMNS];
  } catch {
    return [...COMPRAS_E_DEFAULT_TABLE_COLUMNS];
  }
};

export const saveComprasETableColumns = (selected: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      COMPRAS_E_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify({
        selected: sanitizeComprasETableColumns(selected),
        catalogIds: [...COMPRAS_E_DEFAULT_TABLE_COLUMNS],
      }),
    );
  } catch {
    // quota / private mode — ignore
  }
};
